import { useIntl } from '@umijs/max';
import { Button } from 'antd';
import classNames from 'classnames';
import { useState } from 'react';
import type { CallBackProps, Step, TooltipRenderProps } from 'react-joyride';
import ReactJoyride from 'react-joyride';
import styles from './styles.less';

export enum InstructionStorageKeys {
  'Dashboard' = 'first-time-dashboard',
  'User' = 'first-time-user',
}

export const toothNumberForInstruction = 11;

export type IntroductionProps = {
  storageKey: InstructionStorageKeys;
  steps: Step[];
  disableScrolling?: boolean;
};

export const showInstruction = (
  key: InstructionStorageKeys,
  defaultValue = true,
): boolean =>
  JSON.parse(localStorage.getItem(key) ?? JSON.stringify(defaultValue));

export const setShowInstruction = (
  key: InstructionStorageKeys,
  value: boolean,
) => localStorage.setItem(key, JSON.stringify(value));

export const Introduction = (props: IntroductionProps) => {
  const { storageKey, steps, disableScrolling = true } = props;

  const [showIntroduction, setShowIntroduction] = useState(
    showInstruction(storageKey),
  );

  const handleCallback = (data: CallBackProps) => {
    if (data.status === 'skipped' || data.status === 'finished') {
      setShowIntroduction(false);
      setShowInstruction(storageKey, false);
    }
  };

  return (
    <ReactJoyride
      steps={steps}
      showProgress={true}
      showSkipButton={true}
      continuous={true}
      run={showIntroduction}
      tooltipComponent={IntroductionTooltip}
      callback={handleCallback}
      scrollToFirstStep={true}
      disableScrolling={disableScrolling}
    />
  );
};

const IntroductionTooltip = (props: TooltipRenderProps) => {
  const {
    step,
    size,
    index,
    continuous = true,
    backProps,
    skipProps,
    primaryProps,
    tooltipProps,
  } = props;

  const intl = useIntl();

  const nextButtonLabel =
    index + 1 === size
      ? intl.formatMessage({
          id: 'components.introduction.button.close',
          defaultMessage: 'Đóng',
        })
      : intl.formatMessage(
          {
            id: 'components.introduction.button.next',
            defaultMessage: 'Tiếp theo {progress}',
          },
          {
            progress: `${index + 1}/${size}`,
          },
        );
  const showSkipButton = index + 1 !== size;

  return (
    <div className={styles.TooltipRoot} {...tooltipProps}>
      {step.title && <div className={styles.TooltipTitle}>{step.title}</div>}
      <div className={styles.TooltipContent}>{step.content}</div>
      <div
        className={classNames(styles.TooltipActions, {
          [styles.hasOnlyOneButton]: !showSkipButton,
        })}
      >
        {showSkipButton && (
          <Button type="text" {...skipProps}>
            {intl.formatMessage({
              id: 'components.introduction.button.skip',
              defaultMessage: 'Bỏ qua',
            })}
          </Button>
        )}

        <div>
          {index > 0 && (
            <Button type="text" className={styles.BackButton} {...backProps}>
              {intl.formatMessage({
                id: 'components.introduction.button.back',
                defaultMessage: 'Quay lại',
              })}
            </Button>
          )}
          {continuous && (
            <Button type="primary" {...primaryProps}>
              {nextButtonLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const introductionsTarget = {
  dashboard: {
    profileButton: '.intro-profile-button',
    languageButton: '.intro-language-button',
    noticeButton: '.intro-notice-button',
    rightMenu: '.ant-menu-root',
    hideMenu: '.ant-pro-sider-collapsed-button .ant-menu-title-content span',
  },
  user: {
    filter: '.ant-pro-table-search-query-filter',
    filterMore: '.pro-form-query-filter-actions .ant-pro-form-collapse-button',
    tableSetting: '.ant-pro-table-list-toolbar-setting-items',
  },
};
